import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../scripts/AuthContext";
import AuthShell from "@/components/brand/AuthShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { API_BASE_URL } from "@/lib/apiBase";
import { getFullName } from "@/lib/userUtils";
import { withInviteReturn } from "@/lib/inviteReturn";

/** Survives Strict Mode remount so we don't double-accept the same invite. */
const autoAcceptStarted = new Set();

const InviteAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { accessToken, isAuthenticated, logout } = useAuth();
  const [preview, setPreview] = useState(null);
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/invitations/token/${encodeURIComponent(token)}`
        );
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) {
          setPreview(null);
          setError(
            err.response?.data?.message || "This invitation is not valid."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!accessToken) {
      setMe(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/user/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!cancelled) setMe(data);
      } catch {
        if (!cancelled) setMe(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const handleJoin = async () => {
    if (!accessToken || joining) return;
    setJoining(true);
    setError(null);
    try {
      await axios.post(
        `${API_BASE_URL}/invitations/accept-token`,
        { token },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const orgId = preview?.org_id;
      navigate(orgId ? `/orgs/${encodeURIComponent(orgId)}` : "/dashboard");
    } catch (err) {
      const msg =
        err.response?.data?.message || "Could not accept this invitation.";
      setError(msg);
    } finally {
      setJoining(false);
    }
  };

  const handleSwitchAccount = async () => {
    await logout();
  };

  const orgName = preview?.org_name || "an organization";
  const expired = Boolean(preview?.expired);
  const alreadyDone =
    preview && preview.status && preview.status !== "pending";
  const canJoin =
    isAuthenticated && preview && !expired && !alreadyDone && preview.status === "pending";
  const returnPath = `/invite/${token}`;
  const displayName = getFullName(me) || me?.email || "this Circa account";

  useEffect(() => {
    if (!canJoin || !accessToken || !token) return;
    if (autoAcceptStarted.has(token)) return;
    autoAcceptStarted.add(token);
    void handleJoin();
    // Run once per invite token when canJoin becomes true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canJoin, accessToken, token]);

  return (
    <AuthShell
      title="Organization invite"
      subtitle={
        loading
          ? "Loading invitation…"
          : `You've been invited to join ${orgName} on Circa.`
      }
      footer={
        <p>
          <Link to="/about" className="font-medium text-primary hover:underline">
            About Circa
          </Link>
        </p>
      }
    >
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading invitation…</p>
      ) : !preview ? null : (
        <div className="space-y-4">
          {preview.inviter_name ? (
            <p className="text-sm text-muted-foreground">
              Invited by {preview.inviter_name}
            </p>
          ) : null}
          {expired ? (
            <p className="text-sm text-muted-foreground">
              This invitation has expired. Ask an admin to send a new one.
            </p>
          ) : alreadyDone ? (
            <p className="text-sm text-muted-foreground">
              This invitation is no longer pending.
            </p>
          ) : isAuthenticated ? (
            <>
              <p className="text-sm text-foreground">
                {joining && !error ? (
                  <>Joining {orgName}…</>
                ) : (
                  <>
                    Join <span className="font-medium">{orgName}</span> as{" "}
                    <span className="font-medium">{displayName}</span>
                    {me?.email ? (
                      <>
                        {" "}
                        (<span className="font-medium">{me.email}</span>)
                      </>
                    ) : null}
                    ?
                  </>
                )}
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  className="circa-glow"
                  disabled={!canJoin || joining}
                  onClick={handleJoin}
                >
                  {joining ? "Joining…" : "Join organization"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={joining}
                  onClick={handleSwitchAccount}
                >
                  Use a different account
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button asChild className="circa-glow">
                <Link to={withInviteReturn("/login", returnPath)}>Sign in</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to={withInviteReturn("/signup", returnPath)}>
                  Create account
                </Link>
              </Button>
            </div>
          )}
        </div>
      )}
    </AuthShell>
  );
};

export default InviteAcceptPage;
